import { useState, useEffect } from 'react';
import { useTranslation } from '@stores/translationStore';
import { apiClient } from '@services/api';
import { useSignalR } from '@hooks/useSignalR';
import { playSound } from '../services/soundService';
import PageSpinner from '@components/PageSpinner';
import { QRCodeSVG } from 'qrcode.react';
import PageHeader from '@components/PageHeader';
import SectionTitle from '@components/SectionTitle';
import ServiceCard from '@components/ServiceCard';
import './VouchersPage.scss';

// Helper to get hotel slug
const getHotelSlug = (): string => {
  // Try localStorage first (most reliable)
  const stored = localStorage.getItem('gcxp_hotel_slug');
  if (stored) return stored;
  // Fallback to URL path
  const path = window.location.pathname.split('/').filter(Boolean);
  return path[0] || '';
};

interface Voucher {
  id: string;
  token: string;
  templateName: string;
  description?: string;
  valueType: string;
  valueAmount?: number;
  benefitDescription?: string;
  issuedAt: string;
  status: string;
  maxRedemptions: number;
  currentRedemptions: number;
  expiresAt: string;
  eligibleFacilities: { id: number; name: string; type: string }[];
}

const statusLabels: Record<string, { label: string; class: string }> = {
  Active: { label: 'Active', class: 'voucher-status--active' },
  FullyRedeemed: { label: 'Used', class: 'voucher-status--used' },
  Expired: { label: 'Expired', class: 'voucher-status--expired' },
  Cancelled: { label: 'Cancelled', class: 'voucher-status--cancelled' },
};

export default function VouchersPage() {
  const { t } = useTranslation();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const { on } = useSignalR();

  useEffect(() => {
    const controller = new AbortController();
    window.scrollTo(0, 0);
    loadVouchers(controller.signal);
    return () => controller.abort();
  }, []);

  // Listen for new vouchers via SignalR
  useEffect(() => {
    const unsub = on('VoucherReceived', () => {
      playSound('notification');
      loadVouchers();
    });
    return () => unsub();
  }, [on]);

  const loadVouchers = async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const hotelSlug = getHotelSlug();
      const res = await apiClient.get<Voucher[]>(`/api/v1/${hotelSlug}/session/vouchers`, { signal });
      setVouchers(res.data);
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === 'AbortError') return;
      setError((e as Error)?.message || 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (voucher: Voucher) => {
    if (voucher.valueType === 'FixedAmount') {
      return `₪${voucher.valueAmount}`;
    }
    if (voucher.valueType === 'Percentage') {
      return `${voucher.valueAmount}% off`;
    }
    return voucher.benefitDescription || 'Benefit';
  };

  const formatExpiry = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return 'Expires tomorrow';
    if (diffDays <= 7) return `Expires in ${diffDays} days`;
    return `Expires ${date.toLocaleDateString()}`;
  };

  if (loading) {
    return <PageSpinner />;
  }

  if (error) {
    return (
      <div className="vouchers-page">
        <div className="vouchers-error">
          <p>{error}</p>
          <button onClick={() => loadVouchers()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const activeVouchers = vouchers.filter(v => v.status === 'Active');
  const usedVouchers = vouchers.filter(v => v.status !== 'Active');

  const renderVoucherValue = (voucher: Voucher) => {
    if (voucher.valueType === 'FixedAmount') return `₪${voucher.valueAmount}`;
    if (voucher.valueType === 'Percentage') return `${voucher.valueAmount}%`;
    return '🎁';
  };

  return (
    <div className="page-content vouchers-page">
      <PageHeader
        title={t('vouchers.title') || 'My Vouchers'}
        subtitle={t('vouchers.subtitle') || 'Show QR code to redeem at facilities'}
      />

      <div className="py-3">
        {vouchers.length === 0 ? (
          <div className="text-center py-5" style={{ color: 'var(--surface-text-muted)' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎫</div>
            <h3 className="h5 mb-2" style={{ color: 'var(--surface-text)' }}>
              {t('vouchers.noVouchers') || 'No vouchers yet'}
            </h3>
            <p className="small mb-0">{t('vouchers.noVouchersDesc') || 'Vouchers will appear here when issued by the hotel'}</p>
          </div>
        ) : (
          <>
            {activeVouchers.length > 0 && (
              <section>
                <SectionTitle>{t('vouchers.available') || 'Available'}</SectionTitle>
                {activeVouchers.map(voucher => (
                  <ServiceCard
                    key={voucher.id}
                    emoji={renderVoucherValue(voucher)}
                    title={voucher.templateName}
                    description={
                      <>
                        {voucher.benefitDescription && <span>{voucher.benefitDescription} · </span>}
                        <span>{formatExpiry(voucher.expiresAt)}</span>
                      </>
                    }
                    onClick={() => setSelectedVoucher(voucher)}
                    trailing={
                      <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                        {voucher.currentRedemptions}/{voucher.maxRedemptions}
                      </span>
                    }
                  />
                ))}
              </section>
            )}

            {usedVouchers.length > 0 && (
              <section style={{ opacity: 0.7 }}>
                <SectionTitle>{t('vouchers.used') || 'Used / Expired'}</SectionTitle>
                {usedVouchers.map(voucher => (
                  <ServiceCard
                    key={voucher.id}
                    emoji="🎫"
                    title={voucher.templateName}
                    trailing={
                      <span className={`voucher-status ${statusLabels[voucher.status]?.class || ''}`}>
                        {statusLabels[voucher.status]?.label || voucher.status}
                      </span>
                    }
                    noChevron
                  />
                ))}
              </section>
            )}
          </>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedVoucher && (
        <div className="voucher-modal-overlay" onClick={() => setSelectedVoucher(null)} role="dialog" aria-modal="true" aria-label={selectedVoucher.templateName}>
          <div className="voucher-modal" onClick={e => e.stopPropagation()}>
            <button className="voucher-modal-close" onClick={() => setSelectedVoucher(null)}>×</button>
            
            <h2 className="voucher-modal-title">{selectedVoucher.templateName}</h2>
            
            {selectedVoucher.benefitDescription && (
              <p className="voucher-modal-benefit">{selectedVoucher.benefitDescription}</p>
            )}

            <div className="voucher-modal-value">
              {formatValue(selectedVoucher)}
            </div>

            <div className="voucher-modal-qr">
              <QRCodeSVG value={selectedVoucher.token} size={200} level="H" />
            </div>

            <div className="voucher-modal-code">{selectedVoucher.token}</div>

            <div className="voucher-modal-info">
              <p>
                <strong>{t('vouchers.uses') || 'Uses'}:</strong> {selectedVoucher.currentRedemptions}/{selectedVoucher.maxRedemptions}
              </p>
              <p>
                <strong>{t('vouchers.expires') || 'Expires'}:</strong> {new Date(selectedVoucher.expiresAt).toLocaleDateString()}
              </p>
            </div>

            {selectedVoucher.eligibleFacilities?.length > 0 && (
              <div className="voucher-modal-facilities">
                <p className="voucher-modal-facilities-title">{t('vouchers.validAt') || 'Valid at'}:</p>
                <ul>
                  {selectedVoucher.eligibleFacilities.map(f => (
                    <li key={f.id}>{f.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
